import { Card } from "$root/components/EvolvedPrint/components/Card";
import type { PrintableProps } from "$root/components/EvolvedPrint/configuration/types";
import GraphLegend from "$root/components/GraphLegend";
import ColoredRectangle from "$root/components/icons/ColoredRectangle";
import DashedRectangle from "$root/components/icons/DashedRectangle";
import { Highcharts } from "$root/third-party-integrations/highcharts-with-modules";
import HighchartsReact from "highcharts-react-official";
import { approxRound, builtInSortFnFor } from "@mdotm/shadcn/legacy/utils";
import { useEffect, useMemo, useRef } from "react";
import { useReportTranslation } from "../translation";
import type { PerformanceSeries, RealizedReturn } from "$root/widgets-architecture/widgets/PortfolioPerformance/type";
import { minMaxArrayLike } from "$root/utils";

const CustomizedChart: React.FC<{
	performanceSeries: Omit<PerformanceSeries, "benchmarkPerformance"> & {
		benchmarkPerformance?: PerformanceSeries["benchmarkPerformance"];
	};
}> = ({ performanceSeries }) => {
	const { t } = useReportTranslation();

	const colorCategories = useMemo(
		() => [
			{
				name: t("REPORT_BUILDER.PERFORMANCE.LEGENDA.CURRENT_PERFORMANCE"),
				color: "#00AEEF",
				Component: ColoredRectangle,
			},
			{
				name: t("REPORT_BUILDER.PERFORMANCE.LEGENDA.BENCHMARK"),
				color: "#8C8EA8",
				Component: DashedRectangle,
			},
		],
		[t],
	);
	const chartRef = useRef<HighchartsReact.RefObject>(null);

	useEffect(() => {
		if (chartRef.current) {
			chartRef.current.chart.reflow();
			chartRef.current.chart.redraw();
		}
	}, []);

	const series = useMemo(() => {
		const sortedPerformance = performanceSeries.portfolioPerformance
			.slice()
			.sort(builtInSortFnFor("dateInMilliseconds"));

		const sortedBenchmark = (performanceSeries.benchmarkPerformance ?? [])
			.slice()
			.sort(builtInSortFnFor("dateInMilliseconds"));

		const perfScale = Math.max(0.00001, Math.abs(sortedPerformance[0]?.performance ?? 0));
		const benchScale = Math.max(0.00001, Math.abs(sortedBenchmark?.[0]?.performance ?? 0));

		return {
			portfolioPerformance: sortedPerformance.map((s) => ({
				x: s.dateInMilliseconds,
				y: approxRound(100 * (s.performance / perfScale - 1), 2),
			})),
			benchmarkPerformance: sortedBenchmark?.map((s) => ({
				x: s.dateInMilliseconds,
				y: approxRound(100 * (s.performance / benchScale - 1), 2),
			})),
		};
	}, [performanceSeries]);

	const performanceRangeWholeSeries = useMemo(() => {
		const perf = minMaxArrayLike(series.portfolioPerformance, (x) => x.y);
		const bench = minMaxArrayLike(series.benchmarkPerformance ?? [], (x) => x.y);

		const min = Math.min(perf.min, bench.min);
		const max = Math.max(perf.max, bench.max);
		return { min, max };
	}, [series]);

	const options = useMemo(() => {
		return {
			chart: {
				style: {
					fontFamily: "Gotham,sans-serif",
				},
				panning: {
					enabled: false,
				},
				reflow: true,
			},
			credits: {
				enabled: false,
			},
			legend: {
				enabled: false,
			},
			title: {
				text: undefined,
			},
			subtitle: {
				text: undefined,
			},
			navigator: {
				enabled: false,
			},
			scrollbar: {
				enabled: false,
			},
			exporting: {
				enabled: false,
				floating: false,
			},
			rangeSelector: {
				// inputEnabled: true,
				// allButtonsEnabled: false,
				// enabled or disabled
				enabled: false,
			},
			yAxis: {
				opposite: false,
				title: {
					enabled: false,
				},
				tickAmount: 6,
				startOnTick: false,
				endOnTick: false,
				softMin: performanceRangeWholeSeries.min,
				softMax: performanceRangeWholeSeries.max,
				gridLineWidth: 1,
				gridLineColor: "#CCCCCC",
			},
			tooltip: { enabled: false },
			plotOptions: {
				series: {
					animation: {
						duration: 0,
					},
					turboThreshold: 0,
				},
			},
			series: [
				{
					data: series.portfolioPerformance,
					color: colorCategories[0].color,
					marker: {
						fillColor: colorCategories[0].color,
					},
					name: t("PORTFOLIO_PERFORMANCE.CURRENT_PERF"),
				},
				...(series.benchmarkPerformance === undefined
					? []
					: [
							{
								data: series.benchmarkPerformance,
								color: colorCategories[1].color,
								dashStyle: "Dash",
								marker: {
									fillColor: colorCategories[1].color,
								},
								name: t("PORTFOLIO_PERFORMANCE.BENCHMARK"),
							},
						]),
			],
		};
	}, [
		colorCategories,
		series.benchmarkPerformance,
		series.portfolioPerformance,
		t,
		performanceRangeWholeSeries.min,
		performanceRangeWholeSeries.max,
	]);

	return (
		<div className="h-[250px]">
			<HighchartsReact
				containerProps={{ style: { height: "calc(100% - 75px)" } }}
				highcharts={Highcharts}
				constructorType="stockChart"
				options={options}
				ref={chartRef}
			/>
			<div>
				<GraphLegend style={{ position: "static", margin: 0, paddingLeft: "16px" }}>
					{colorCategories.map(({ name, color, Component }) => (
						<div key={color} className="legend-container light more-space book">
							<Component variant="vertical" color={color} />
							{name}
						</div>
					))}
				</GraphLegend>
			</div>
		</div>
	);
};

export function PortfolioPerformance(
	props: PrintableProps<
		{
			current: [/* timestamp */ number, /* value */ number][];
			benchmark?: [/* timestamp */ number, /* value */ number][];
			showBenchmark: boolean;
		},
		null
	>,
): React.JSX.Element {
	const { t } = useReportTranslation();

	const performanceSeries = useMemo(() => {
		return {
			portfolioPerformance: props.current.map(
				([dateSeconds, x]): RealizedReturn => ({
					dateInMilliseconds: dateSeconds * 1000,
					return: x, // -100..>100
					performance: x / 100 + 1, // -1..>1 => +1 => 0..>2
				}),
			),
			benchmarkPerformance: !props.showBenchmark
				? undefined
				: (props.benchmark?.map(
						([dateSeconds, x]): RealizedReturn => ({
							dateInMilliseconds: dateSeconds * 1000,
							return: x, // -100..>100
							performance: x / 100 + 1, // -1..>1 => +1 => 0..>2
						}),
					) ?? []),
		};
	}, [props]);

	return (
		<Card title={t("REPORT_BUILDER.PERFORMANCE.TITLE")}>
			<CustomizedChart performanceSeries={performanceSeries} />
		</Card>
	);
}
